import { getTotalPayoutImageTag } from '@monorepo/client-common';
import {
  Achievement,
  NOT_AVAILABLE,
  StorageContentType,
} from '@monorepo/types';
import { getEnv } from '@hub/helpers/getters.store';
import AchievementService from '@hub/services/entities/achievement-service';
import { EntityStore } from '../base/entity-store';

export default class AchievementStore extends EntityStore<
  AchievementService,
  Achievement
> {
  constructor() {
    const {
      apiFactory: { achievementService },
    } = getEnv();

    super(achievementService);
  }

  getDisplayName({
    firstName,
    lastName,
    userName,
    hasAccess = true,
  }: {
    userName?: string;
    firstName?: string;
    lastName?: string;
    hasAccess?: boolean;
  }) {
    if (!hasAccess) {
      return NOT_AVAILABLE;
    }

    const toFirstName = firstName ? firstName.trim() : '';
    const toLastName = lastName ? lastName.trim() : '';
    const result = `${toFirstName} ${toLastName}`.trim();

    return result || userName || NOT_AVAILABLE;
  }

  async getAchievements(publicId?: string) {
    const {
      apiFactory: { achievementService },
    } = getEnv();

    return await achievementService.getAchievements(publicId);
  }

  async createShareableImage(achievement: Achievement, imageUrl: string) {
    const {
      apiFactory: { achievementService },
    } = getEnv();
    if (achievement.link) {
      return achievement.link;
    }

    const { shareableUrl } = await achievementService.createShareableImage(
      achievement._id,
      imageUrl,
    );

    achievement.link = shareableUrl;
    return shareableUrl;
  }

  async createTotalPayoutImage({
    imageUrl,
    amount,
    userId,
  }: {
    amount?: number;
    imageUrl: string;
    userId: string;
  }) {
    const {
      apiFactory: { achievementService, assetsService },
    } = getEnv();

    if (amount === undefined) {
      throw new Error('Invalid amount provided for total payout image');
    }

    const urlExists = await achievementService.checkIfTotalPayoutImageExists(
      userId,
      amount,
    );

    if (urlExists) {
      return urlExists;
    }

    const tag = getTotalPayoutImageTag(userId, amount);

    return await assetsService.uploadTotalPayoutImage({
      type: StorageContentType.Achievements,
      imageUrl,
      tag,
      publicId: tag,
    });
  }
}
